import { useMemo } from 'react';
import useSWR from 'swr';
import { formatDisplayDate } from '@/helpers';
import { client, fetcher } from '@/plugins/graphql';
import { RentalFacility, SchoolRentalCloseDay } from '@/types/Rental';

type UseRentalFacility = [
  {
    rentalFacility: RentalFacility | null;
    mutate: any;
    update: (id: number, rentalData: RentalFacility, closeDayData: SchoolRentalCloseDay[]) => Promise<void>;
  },
];

const useRentalFacility = (id: number, schoolId: number): UseRentalFacility => {
  const query = useMemo(() => {
    return `
      {
        admin_rental_facility(where: {
            school_rental: {
              school: {
                id: {
                  _eq: ${schoolId}
                }
              }
            }
            id: {_eq: ${id}},
            deleted_at: {_is_null: true}
          }
        ) {
          id
          name
          img_ids
          description
          info
          city
          address
          tel
          business_hour_start
          business_hour_end
          is_publish
          cancellation_deadline
          cancellation_policy
          updated_at
          rental_closed_days(
            where: {deleted_at: {_is_null: true}}
          ) {
            id
            closed_day
            repeat_type
          }
        }
      }
    `;
  }, [id, schoolId]);
  const { data, mutate } = useSWR(id ? query : null, fetcher, { revalidateOnFocus: false });

  const returnData = useMemo(() => {
    if (!data) return data;
    else {
      return data['admin_rental_facility'].length > 0 ? data['admin_rental_facility'][0] : null;
    }
  }, [data]);

  const update = async (id: number | null, rentalData: RentalFacility, closeDayData: SchoolRentalCloseDay[]) => {
    const closedayObjects = closeDayData
      .reduce((acc: string[], option) => {
        const optionString = Object.entries(option).map(([key, value]) => {
          if (key === 'closed_day') {
            return `${key}:"${formatDisplayDate(new Date(String(value)))}"`;
          } else if ((key === 'deleted_at' && value === null) || key === 'rental_facility_id') {
            return `${key}:${value}`;
          } else {
            return `${key}:"${value}"`;
          }
        });
        return [...acc, `{${optionString}}`];
      }, [])
      .join(',');

    const mutation = `
      mutation UpdateRentalFacility {
        update_admin_rental_facility(where: {
          id: {
            _eq: ${id}
            }
          },
          _set: {
            name: "${rentalData.name}"
            city: "${rentalData.city}"
            address: "${rentalData.address}"
            tel: "${rentalData.tel}"
            business_hour_start: "${rentalData.business_hour_start}"
            business_hour_end: "${rentalData.business_hour_end}"
            description: ${typeof rentalData?.description === 'string' ? `"""${rentalData.description}"""` : null}
            info: ${typeof rentalData?.info === 'string' ? `"""${rentalData.info}"""` : null}
            img_ids: "{${rentalData.img_ids}}"
            is_publish: ${rentalData.is_publish}
            cancellation_deadline: "${rentalData.cancellation_deadline}"
            cancellation_policy: "${rentalData.cancellation_policy}"
          }
        ) {
          returning {
            id
          }
        }
        insert_admin_rental_closed_day(
          objects: [${closedayObjects}],
          on_conflict: {
            constraint: rental_closed_day_pkey,
            update_columns: [closed_day, repeat_type, deleted_at]
          }) {
          returning {
            id
          }
        }
      }
    `;

    return client.request(mutation);
  };

  return [
    {
      rentalFacility: returnData,
      mutate,
      update,
    },
  ];
};

export default useRentalFacility;
